/**
 * Notification Preferences API client — Story 95-9.
 *
 * Thin wrapper over the `/settings/notification-preferences` endpoints. The
 * backend controller lands in a follow-up of Story 95-9 (the same story
 * owns the UI); until then the hooks degrade gracefully — a 404 translates
 * into an empty preference list so the settings page still renders and
 * the user can save a starting preference row.
 *
 * @see packages/integration/messaging-gateway/src/notification/services/notification-preference.service.ts
 */

import { api } from './client';

/** 4 channel types — KEEP IN SYNC with `NOTIFICATION_CHANNELS` in the DTO. */
export type NotificationChannel = 'email' | 'sms' | 'in-app' | 'webhook';

export interface NotificationPreferenceRow {
  /** Catalog key — e.g. `'vehicle.document.expiring'`. */
  notificationType: string;
  /** Channels the user wants this type on. Defaults from catalog when empty. */
  enabledChannels: NotificationChannel[];
  /** Local-time HH:mm start of daily quiet hours. `null` = not set. */
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  /** IANA zone, e.g. `'Europe/Budapest'`. `null` = inherit from user profile. */
  timezone: string | null;
}

export interface UpsertNotificationPreferenceInput {
  notificationType: string;
  enabledChannels: NotificationChannel[];
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  timezone: string | null;
}

export async function listMyNotificationPreferences(): Promise<NotificationPreferenceRow[]> {
  try {
    const res = await api.get<{ data: NotificationPreferenceRow[] }>(
      '/settings/notification-preferences'
    );
    return res.data ?? [];
  } catch (err: unknown) {
    // 404 = controller not yet wired. Return empty list so the UI can
    // still paint and let the user persist a first-time preference row.
    if (isHttpNotFound(err)) {
      return [];
    }
    throw err;
  }
}

export async function upsertNotificationPreference(
  input: UpsertNotificationPreferenceInput
): Promise<NotificationPreferenceRow> {
  // Backend semantics are upsert: PATCH-by-key maps cleanly to Prisma's
  // `upsert({ where: { tenantId_userId_notificationType }, update, create })`
  // in `PrismaNotificationPreferenceRepository`.
  const res = await api.patch<{ data: NotificationPreferenceRow }>(
    '/settings/notification-preferences',
    input
  );
  return res.data;
}

/**
 * Admin reset — wipes all `NotificationPreference` rows for the given user
 * and restores catalog defaults. Requires FRANCHISE_ADMIN role (enforced
 * server-side). Returns the number of rows deleted so the UI can show a
 * toast like "3 preference row-t visszaállítottunk".
 */
export async function resetUserNotificationPreferences(
  userId: string
): Promise<{ deleted: number }> {
  const res = await api.delete<{ data: { deleted: number } }>(
    `/settings/notification-preferences/${encodeURIComponent(userId)}`
  );
  return res.data;
}

function isHttpNotFound(err: unknown): boolean {
  if (err instanceof Error && 'status' in err) {
    return (err as Error & { status?: number }).status === 404;
  }
  return false;
}
